import { AstarNode } from "./AstarNode.js";
import { FibonacciHeap } from '@tyriar/fibonacci-heap';
export { WeightedAstar };
/**
 * Implements A* graph search functionality
 * Uses a heuristic to approximate which descendant nodes are most likely nearest to the target
 * and also takes into account the path cost (=length) up to now
 */
class WeightedAstar {
    /**
     * Performs A* graph search with on-the-fly expansion of the graph nodes
     * @param {Node2D} startNode - point and direction of the starting position
     * @param {Function} targetFunction - tests whether target is found, cf. TargetFunctions.js
     * @param {Function} expandNode - expands a node (computes possible descendents)
     * @param {Function} heuristic - approximates the quality of a node, cf. Heuristics.js
     * @param {Function} checkCollision - checks for collisions, cf. CollisionCheck.js
     * @param {number} epsilon - factor to scale (the importance of) the heuristic; if > 1, bias towards nodes that are closer to the goal
     * @param {number} maxIterations - limit of main loop iterations to prevent infinite loops
     * @return {Promise<any[] | undefined>} the target node
     */
    static shortestPath({ startNodes, isTarget, successors, heuristic, epsilon = 1, maxIterations = 25000 }) {
        var _a;
        // Open list is the set of unexpanded nodes with the most promising (= least cost estimate) on front
        // i.e. a priority queue
        const openList = new FibonacciHeap();
        // Define start node, previous is undefined, cost is 0
        if (startNodes instanceof Array) {
            for (let startNode of startNodes) {
                const initial = new AstarNode(undefined, startNode, 0);
                openList.insert(heuristic(startNode, initial), initial);
            }
        }
        else {
            const initial = new AstarNode(undefined, startNodes, 0);
            openList.insert(heuristic(startNodes, initial), initial);
        }
        // Start main loop
        let i = 0;
        let target = undefined;
        while (!openList.isEmpty() && i < maxIterations) {
            // Take most promising unexpanded node from the open list
            const candidate = (_a = openList.extractMinimum()) === null || _a === void 0 ? void 0 : _a.value;
            // Check whether the candidate is actually the target, if true stop
            if (isTarget(candidate.node, candidate)) {
                target = candidate;
                break;
            }
            // Expand the node, add its children to the open list
            const s = successors(candidate.node, candidate), successorNodes = s.map(n => new AstarNode(candidate, n.node, n.cost + candidate.cost));
            for (let successor of successorNodes) {
                // The new estimate is the cost/length of the new node plus the heurstic distance
                const estimate = successor.cost + epsilon * heuristic(successor.node, successor);
                openList.insert(estimate, successor);
            }
            i++;
        }
        if (target === undefined) {
            return target;
        }
        return target.getNodes();
    }
}
//# sourceMappingURL=WeightedAstar.js.map